import { fetchEventSource } from '@microsoft/fetch-event-source'
import { parseBusinessOrderExecution } from '~/lib/sse/parseBusinessOrderExecution'
import { parseBusinessOrderMessage } from '~/lib/sse/parseBusinessOrderMessage'
import { dispatchSseEvent, subscribeSseEvent } from '~/lib/sse/sseEventBus'
import type { BusinessOrderExecutionPatch } from '~/lib/orders/activeExecutionsCache'
import type { OrderChatMessage } from '~/types/order-chat-message'
import { getSseUrl } from '~/utils/sse'

const BUSINESS_ORDER_MESSAGE_EVENT = 'business-order-message'
const BUSINESS_ORDER_EXECUTION_EVENT = 'business-order-execution'

type BusinessOrderMessageHandler = (message: OrderChatMessage) => void
type BusinessOrderExecutionHandler = (execution: BusinessOrderExecutionPatch) => void

let abortController: AbortController | null = null
let isConnecting = false
let watchInitialized = false

async function connect(token: string) {
    if (abortController || isConnecting) {
        return
    }

    isConnecting = true
    const controller = new AbortController()
    abortController = controller

    try {
        await fetchEventSource(getSseUrl(), {
            signal: controller.signal,
            headers: {
                Accept: 'text/event-stream',
                Authorization: `Bearer ${token}`,
            },
            onopen: async (response) => {
                if (response.ok && response.headers.get('content-type')?.includes('text/event-stream')) {
                    isConnecting = false
                    return
                }
                if (response.status === 401) {
                    stop()
                    throw new Error('Unauthorized')
                }
                isConnecting = false
                throw new Error(`SSE connection failed: ${response.status}`)
            },
            onmessage: (event) => {
                if (!event.data) {
                    return
                }
                if (event.event === BUSINESS_ORDER_MESSAGE_EVENT) {
                    const message = parseBusinessOrderMessage(event.data)
                    if (message) {
                        dispatchSseEvent(BUSINESS_ORDER_MESSAGE_EVENT, message)
                    }
                    return
                }
                if (event.event === BUSINESS_ORDER_EXECUTION_EVENT) {
                    const execution = parseBusinessOrderExecution(event.data)
                    if (execution) {
                        dispatchSseEvent(BUSINESS_ORDER_EXECUTION_EVENT, execution)
                    }
                }
            },
            onclose: () => {
                abortController = null
                isConnecting = false
            },
            onerror: () => {
                abortController = null
                isConnecting = false
                throw new Error('SSE connection error')
            },
        })
    }
    catch {
        abortController = null
        isConnecting = false
    }
}

function stop() {
    if (abortController) {
        abortController.abort()
        abortController = null
    }
    isConnecting = false
}

export function useSseEvents() {
    const token = useCookie<string | null>('access_token')

    function start() {
        if (watchInitialized) {
            return
        }
        watchInitialized = true

        watch(token, (value) => {
            if (value) {
                void connect(value)
            }
            else {
                stop()
            }
        }, { immediate: true })
    }

    function onBusinessOrderMessage(handler: BusinessOrderMessageHandler) {
        return subscribeSseEvent(BUSINESS_ORDER_MESSAGE_EVENT, handler as (data: unknown) => void)
    }

    function onBusinessOrderExecution(handler: BusinessOrderExecutionHandler) {
        return subscribeSseEvent(BUSINESS_ORDER_EXECUTION_EVENT, handler as (data: unknown) => void)
    }

    return {
        start,
        stop,
        onBusinessOrderMessage,
        onBusinessOrderExecution,
    }
}
